import { Component, OnInit, OnDestroy, ViewChild, ChangeDetectorRef, HostListener, ElementRef, TemplateRef } from '@angular/core';
import { Subject } from 'rxjs';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { ToastrService } from 'ngx-toastr';
import { MatDialog } from '@angular/material/dialog';
import { WaterSubDevAreaService } from '../../services/water-sub-dev-area.service';
import { 
  WaterSubDevAreaRow, 
  WaterSubDevAreaDetailsRequest,
  WaterSubDevAreaDropDownResult,
  TableColumn,
  FilterColumn
} from '../../services/interfaces/water-sub-dev-area.interfaces';
import { AuthService } from '../../services/auth.service';
import { ErrorHandler } from '../../utils/error-handler';
import { exportRowsToExcel } from '../../utils/excel-export';

@Component({
  selector: 'app-water-sub-dev-area',
  standalone: false,
  templateUrl: './water-sub-dev-area.html',
  styleUrl: './water-sub-dev-area.scss',
})
export class WaterSubDevArea implements OnInit, OnDestroy {
  @ViewChild(CdkVirtualScrollViewport) viewport!: CdkVirtualScrollViewport;
  @ViewChild('floatingFilterPanel') floatingFilterPanel!: ElementRef;
  @ViewChild('deleteConfirmDialog') deleteConfirmDialog!: TemplateRef<any>;
  @ViewChild('exportOptionsDialog') exportOptionsDialog!: TemplateRef<any>;

  spinnerBoolean: boolean = false;
  data: WaterSubDevAreaRow[] = [];
  selectedRows: WaterSubDevAreaRow[] = [];
  showCancel: boolean = false;
  loading: boolean = false;
  hoveredRowIndex: number | null = null;

  // Pagination tracking
  nextRowNumber: number | null = 0;
  hasMore: boolean = true;

  // Dropdown options
  developmentAreaOptions: string[] = [];
  ctbOptions: string[] = [];

  // Filter-related properties
  filterColumnsData: { [key: string]: string[] } = {};
  isLoadingFilterOptions: boolean = false;
  filterSearch: { [key: string]: string } = {};
  activeFilter: string = '';
  showFloatingFilter: boolean = false;
  dropdownPosition = { top: 0, left: 0 };
  multiFilters: { [key: string]: string[] } = {};
  currentFilters: { [key: string]: string | null } = {
    developmentArea: null,
    subDevAreaName: null,
    ctbs: null,
  };

  public dialogMessage: string = '';
  private destroy$ = new Subject<void>();

  columns: TableColumn[] = [
    { field: 'developmentArea', header: 'Dev Area', required: false, filterable: true, filterType: 'multi-select',},
    { field: 'subDevAreaName', header: 'Sub Dev Area', required: false, filterable: true, filterType: 'multi-select' },
    { field: 'ctbs', header: 'CTB', required: false, filterable: true, filterType: 'multi-select', allowMultiple: true },
  ];

  constructor(
    private toastr: ToastrService,
    private waterSubDevAreaService: WaterSubDevAreaService,
    private authService: AuthService,
    private cdr: ChangeDetectorRef,
    private elementRef: ElementRef,
    private dialog: MatDialog
  ) {}

  async ngOnInit(): Promise<void> {
    this.spinnerBoolean = true;
    try {
      await Promise.all([
        this.loadFirstPage(),
        this.loadInitialDropdowns(),
        this.loadFilterOptionsInit(),
      ]);

      this.syncDropdownOptionsFromFilters();
    } finally {
      this.spinnerBoolean = false;
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  async loadInitialDropdowns(): Promise<void> {
    try {
      const dropdownData: WaterSubDevAreaDropDownResult = await this.waterSubDevAreaService.getWaterSubDevAreaDropDown({
        developmentArea: null,
        ctb: null,
      });
      
      this.developmentAreaOptions = dropdownData.developmentAreas || [];
      this.ctbOptions = dropdownData.ctbValues || [];
      this.syncDropdownOptionsFromFilters();

    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async loadFilterOptionsInit(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      this.filterColumnsData = {};
      const response: FilterColumn[] = await this.waterSubDevAreaService.getCascadingFilterOptions({
        developmentArea: null,
        subDevAreaName: null,
        ctb: null,
      });

      if (response && response.length > 0) {
        response.forEach((column: FilterColumn) => {
          const fieldName = this.mapColumnNameToFieldName(column.columnName);
          this.filterColumnsData[fieldName] = column.values || [];
        });
      }

      this.syncDropdownOptionsFromFilters();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  async reloadFilterOptions(): Promise<void> {
    this.isLoadingFilterOptions = true;
    try {
      const returnedColumns = new Set<string>();
      const response: FilterColumn[] = await this.waterSubDevAreaService.getCascadingFilterOptions({
        developmentArea: this.currentFilters['developmentArea'] || null,
        subDevAreaName: this.currentFilters['subDevAreaName'] || null,
        ctb: this.currentFilters['ctbs'] || null,
      });

      if (response && response.length > 0) {
        response.forEach((column: FilterColumn) => {
          const fieldName = this.mapColumnNameToFieldName(column.columnName);
          this.filterColumnsData[fieldName] = column.values || [];
          returnedColumns.add(fieldName);
        });
      }

      Object.keys(this.filterColumnsData).forEach((field) => {
        if (!returnedColumns.has(field)) {
          this.filterColumnsData[field] = [];
        }
      });

      this.syncDropdownOptionsFromFilters();
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to reload filter options';
      this.toastr.error(errorMessage, 'Error');
    } finally {
      this.isLoadingFilterOptions = false;
    }
  }

  private mapColumnNameToFieldName(columnName: string): string {
    const normalized = (columnName || '').trim().toLowerCase();
    const columnMap: { [key: string]: string } = {
      developmentarea: 'developmentArea',
      devarea: 'developmentArea',
      subdevarea: 'subDevAreaName',
      subdevareaname: 'subDevAreaName',
      ctb: 'ctbs',
      wellpropid: 'ctbs',
      wellpropids: 'ctbs',
    };

    return columnMap[normalized] || columnName;
  }

  private uniqueNonEmpty(values: string[]): string[] {
    return Array.from(
      new Set((values || []).map((v) => (v || '').trim()).filter((v) => v.length > 0))
    );
  }

  private syncDropdownOptionsFromFilters(): void {
    this.developmentAreaOptions = this.uniqueNonEmpty([
      ...this.developmentAreaOptions,
      ...(this.filterColumnsData['developmentArea'] || []),
    ]);

    this.ctbOptions = this.uniqueNonEmpty([
      ...this.ctbOptions,
      ...(this.filterColumnsData['ctbs'] || []),
    ]);
  }

  getDevelopmentAreaOptions(): string[] {
    if (this.developmentAreaOptions.length > 0) {
      return this.developmentAreaOptions;
    }
    return this.filterColumnsData['developmentArea'] || [];
  }

  getCtbOptions(): string[] {
    if (this.ctbOptions.length > 0) {
      return this.ctbOptions;
    }
    return this.filterColumnsData['ctbs'] || [];
  }

  private async refreshDropdownOptionsFromCurrentSelection(): Promise<void> {
    const dropdownData: WaterSubDevAreaDropDownResult = await this.waterSubDevAreaService.getWaterSubDevAreaDropDown({
      developmentArea: this.currentFilters['developmentArea'] || null,
      ctb: this.currentFilters['ctbs'] || null,
    });

    this.developmentAreaOptions = dropdownData.developmentAreas || [];
    this.ctbOptions = dropdownData.ctbValues || [];
  }

  private async refreshDropdownOptionsFromRowSelection(row: WaterSubDevAreaRow): Promise<void> {
    const dropdownData: WaterSubDevAreaDropDownResult = await this.waterSubDevAreaService.getWaterSubDevAreaDropDown({
      developmentArea: row.developmentArea || null,
      ctb: row.ctbs.length > 0 ? row.ctbs[0] : null,
    });

    this.developmentAreaOptions = Array.from(new Set([
      ...this.developmentAreaOptions,
      ...dropdownData.developmentAreas || [],
      row.developmentArea
    ])).filter(area => !!area && area.trim() !== '');

    this.ctbOptions = Array.from(new Set([
      ...this.ctbOptions,
      ...dropdownData.ctbValues || [],
      ...row.ctbs
    ])).filter(ctbValue => !!ctbValue && ctbValue.trim() !== '');
  }

  private shouldRefreshRowDropdowns(row: WaterSubDevAreaRow): boolean {
    return row.isNew && !!row.developmentArea?.trim();
  }

  async loadWaterSubDevAreaData(filters?: Partial<WaterSubDevAreaDetailsRequest>): Promise<void> {
    try {
      const filterPayload: WaterSubDevAreaDetailsRequest = {
        developmentArea: this.currentFilters['developmentArea'] || null,
        subDevAreaName: this.currentFilters['subDevAreaName'] || null,
        ctb: this.currentFilters['ctbs'] || null,
        lastRowNumber: filters?.lastRowNumber ?? 0,
        ...filters
      };

      const response = await this.waterSubDevAreaService.getWaterSubDevAreaDetails(filterPayload);
      
      this.nextRowNumber = response.nextRowNumber ?? null;
      this.hasMore = this.nextRowNumber !== null && this.nextRowNumber > filterPayload.lastRowNumber;

      if (response.data && response.data.length > 0) {
        let newRows: WaterSubDevAreaRow[] = response.data.map((item): WaterSubDevAreaRow => ({
          _rowId: this.generateRowId(),
          developmentArea: item.developmentArea,
          subDevAreaName: item.subDevAreaName,
          ctbs: [...item.ctbs],
          isNew: false,
          _rowEdited: false,
          original: {}
        }));

        for (const row of newRows) {
          row.original = { ...row, ctbs: [...row.ctbs] };
        }

        if (filterPayload.lastRowNumber === 0) {
          this.data = newRows;
        } else {
          this.mergeRows(newRows);
        }

      } else {
        if (filterPayload.lastRowNumber === 0) {
          this.data = [];
          if (!this.hasAnyFilterApplied()) {
            this.toastr.info('No existing water sub dev area data found', 'Info');
          }
        }
      }
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to load water sub dev area data';
      this.toastr.error(errorMessage, 'Error');
      this.hasMore = false;
    } finally {
      this.loading = false;
    }
  }

  private mergeRows(newRows: WaterSubDevAreaRow[]): void {
    const keyOf = (r: WaterSubDevAreaRow) =>
      `${r.developmentArea}||${r.subDevAreaName}`;

    const existingKeys = new Set(this.data.map(keyOf));
    const toAdd = newRows.filter((r) => !existingKeys.has(keyOf(r)));
    this.data = [...this.data, ...toAdd];
  }

  private createEmptyRow(): WaterSubDevAreaRow {
    return {
      _rowId: this.generateRowId(),
      developmentArea: '',
      subDevAreaName: '',
      ctbs: [],
      isNew: true,
      _rowEdited: false,
      original: {}
    };
  }

  loadFirstPage(): Promise<void> {
    this.data = [];
    this.nextRowNumber = 0;
    this.hasMore = true;
    return this.loadWaterSubDevAreaData({ lastRowNumber: 0 });
  }

  onScrolledIndexChange(index: number): void {
    const buffer = 10;
    if (
      this.hasMore &&
      !this.loading &&
      !this.spinnerBoolean &&
      index >= Math.max(0, this.data.length - buffer)
    ) {
      this.loading = true;
      this.loadWaterSubDevAreaData({ lastRowNumber: this.nextRowNumber ?? 0 });
    }
  }

  async addRow(): Promise<void> {
    if (!this.developmentAreaOptions.length || !this.ctbOptions.length) {
      this.spinnerBoolean = true;
      try {
        await this.loadInitialDropdowns();
      } finally {
        this.spinnerBoolean = false;
      }
    }

    const newRow: WaterSubDevAreaRow = this.createEmptyRow();
    
    this.data = [newRow, ...this.data];
    this.showCancel = true;
    this.clearSelection();
    
    this.cdr.detectChanges();
    setTimeout(() => {
      this.viewport?.checkViewportSize();
      this.viewport?.scrollToIndex(0, 'smooth');
    });
    
    this.toastr.info('New water sub dev area row added. Click Cancel to discard or Save to keep changes.');
  }

  async handleSave(): Promise<void> {
    const userName = this.authService.getUserName();
    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      const changedRows = this.data.filter(row => row.isNew || row._rowEdited);
      
      if (changedRows.length === 0) {
        this.toastr.info('No changes to save.');
        return;
      }

      this.spinnerBoolean = true;
      
      for (const row of changedRows) {
        const action: 'insert' | 'update' = row.isNew ? 'insert' : 'update';
        const oldSubDevAreaName: string | undefined = action === 'insert' ? undefined : row.original?.subDevAreaName;
        
        const response = await this.waterSubDevAreaService.insertUpdateWaterSubDevArea(row, action, userName, oldSubDevAreaName);
        const parsed = ErrorHandler.parseApiResult(response);

        if (parsed.success) {
          row.isNew = false;
          row._rowEdited = false;
          row.original = { ...row, ctbs: [...row.ctbs] };
          successCount++;
        } else {
          failureCount++;
          errorMessages.push(parsed.message || 'Unknown error occurred');
        }
      }

      await this.loadWaterSubDevAreaData();

      if (successCount > 0) {
        this.toastr.success(`${successCount} water sub dev area row(s) saved successfully`, 'Success');
        this.showCancel = false;
      }

      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} water sub dev area row(s) failed to save`;
        
        this.toastr.error(detailedErrorMsg, 'Save Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to save water sub dev area data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  async handleDelete(): Promise<void> {
    if (!this.selectedRows.length) { 
      this.toastr.info('Please select rows to delete.'); 
      return; 
    }

    const userName = this.authService.getUserName();

    this.dialogMessage = `Are you sure you want to delete ${this.selectedRows.length} row(s)? This action cannot be undone.`;

    const dialogRef = this.dialog.open(this.deleteConfirmDialog, {
      width: '400px',
      disableClose: true,
    });

    const confirmed = await dialogRef.afterClosed().toPromise();
    if (!confirmed) return;

    let successCount = 0;
    let failureCount = 0;
    let errorMessages: string[] = [];

    try {
      this.spinnerBoolean = true;

      for (const row of this.selectedRows) {
        if (row.isNew) {
          const index = this.data.indexOf(row);
          if (index > -1) {
            this.data.splice(index, 1);
          }
          successCount++;
        } else {
          const response = await this.waterSubDevAreaService.removeWaterSubDevAreaDetails(row, userName);
          const parsed = ErrorHandler.parseApiResult(response);

          if (parsed.success) {
            successCount++;
          } else {
            failureCount++;
            errorMessages.push(parsed.message || 'Unknown error occurred');
          }
        }
      }

      this.data = this.data.filter(row => !this.selectedRows.includes(row));
      this.selectedRows = [];

      await this.loadWaterSubDevAreaData();
      
      if (successCount > 0) {
        this.toastr.success(`${successCount} row(s) deleted successfully.`);
      }
      if (failureCount > 0) {
        const detailedErrorMsg = errorMessages.length > 0
          ? errorMessages.join('<br>')
          : `${failureCount} row(s) failed to delete`;
        
        this.toastr.error(detailedErrorMsg, 'Delete Error', {
          timeOut: 8000,
          enableHtml: true
        });
      }
    } catch (error: any) {
      const parsed = ErrorHandler.parseApiResult(error?.error || error);
      this.toastr.error(parsed.message || 'Failed to delete water sub dev area data', 'Error');
    } finally {
      this.spinnerBoolean = false;
    }
  }

  cancelRow(): void {
    this.data = this.data.filter((row) => !row.isNew);
    
    this.data.forEach(row => {
      if (row._rowEdited && row.original && Object.keys(row.original).length > 0) {
        Object.assign(row, { 
          ...row.original, 
          ctbs: [...(row.original.ctbs || [])]
        });
        row._rowEdited = false;
      }
    });
    
    this.showCancel = false;
    this.clearSelection();
    
    this.cdr.detectChanges();
    
    setTimeout(() => {
      this.viewport?.checkViewportSize();
    });
    
    this.toastr.info('Changes cancelled.');
  }

  clearSelection(): void {
    this.selectedRows = [];
  }

  toggleRowSelection(row: WaterSubDevAreaRow, ev: Event): void {
    const target = ev.target as HTMLInputElement;
    if (target.checked) { 
      if (!this.selectedRows.includes(row)) this.selectedRows.push(row); 
    } else { 
      const index = this.selectedRows.indexOf(row); 
      if (index > -1) this.selectedRows.splice(index, 1); 
    }
  }

  markAsEdited(row: WaterSubDevAreaRow): void {
    if (!row.original || Object.keys(row.original).length === 0) {
      row.original = { ...row, ctbs: [...row.ctbs] };
    }
    if (!row.isNew) {
      row._rowEdited = true;
      this.showCancel = true;
    }
  }

  handleEdit(row: WaterSubDevAreaRow): void { 
    this.markAsEdited(row);
  }

  async onDevelopmentAreaSelectionChange(row: WaterSubDevAreaRow): Promise<void> {
    if (!row.isNew) {
      return;
    }

    this.handleEdit(row);
    if (!this.shouldRefreshRowDropdowns(row)) {
      return;
    }

    try {
      await this.refreshDropdownOptionsFromRowSelection(row);
    } catch (error: any) {
      const errorMessage = error.message || 'Failed to refresh dropdown options';
      this.toastr.error(errorMessage, 'Error');
    }
  }

  async addCtb(row: WaterSubDevAreaRow, event: Event): Promise<void> {
    const selectElement = event.target as HTMLSelectElement;
    const selectedCtb = selectElement.value;
    if (selectedCtb && !row.ctbs.includes(selectedCtb)) {
      row.ctbs.push(selectedCtb);
      this.handleEdit(row);
    }
    selectElement.value = '';
  }

  async removeCtb(row: WaterSubDevAreaRow, ctbToRemove: string): Promise<void> {
    const index = row.ctbs.indexOf(ctbToRemove);
    if (index > -1) {
      row.ctbs.splice(index, 1);
      this.handleEdit(row);
    }
  }

  // Filter methods
  async openFloatingFilter(event: MouseEvent, field: string): Promise<void> {
    event.stopPropagation();

    if (this.showFloatingFilter && this.activeFilter === field) {
      this.closeFloatingFilter();
      return;
    }

    if (this.getFilterType(field) === 'multi-select') {
      try {
        await this.reloadFilterOptions();
      } catch (error: any) {
        this.toastr.error(error.message || 'Failed to refresh filter options', 'Error');
      }
    }

    const target = event.target as HTMLElement;
    const rect = target.getBoundingClientRect();
    this.dropdownPosition = {
      top: rect.bottom - 130 + window.scrollY,
      left: rect.left - 90 + window.scrollX,
    };
    this.activeFilter = field;
    this.showFloatingFilter = true;
  }

  closeFloatingFilter(): void {
    this.showFloatingFilter = false;
    this.activeFilter = '';
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (this.showFloatingFilter) {
      const target = event.target as HTMLElement;
      const clickedInside = this.floatingFilterPanel?.nativeElement.contains(target);
      const clickedOnFilterIcon = target.classList.contains('filter-icon') ||
        target.parentElement?.classList.contains('filter-icon');

      if (!clickedInside && !clickedOnFilterIcon) {
        this.closeFloatingFilter();
      }
    }
  }

  getFilterOptions(field: string): string[] {
    if (this.filterColumnsData[field]) {
      const options = this.filterColumnsData[field] || [];
      const search = (this.filterSearch[field] || '').toLowerCase();
      const filtered = options.filter(opt => opt && String(opt).toLowerCase().includes(search));
      return filtered;
    }
    return [];
  }

  toggleMultiFilter(field: string, value: string, event: any): void {
    if (!this.multiFilters[field]) this.multiFilters[field] = [];
    const isChecked = event.target.checked;
    if (isChecked) {
      if (!this.multiFilters[field].includes(value))
        this.multiFilters[field].push(value);
    } else {
      this.multiFilters[field] = this.multiFilters[field].filter(v => v !== value);
    }
  }

  isChecked(field: string, value: string): boolean {
    return this.multiFilters[field]?.includes(value) || false;
  }

  getFilterType(field: string): 'multi-select' | 'search' {
    const column = this.columns.find(col => col.field === field);
    return column?.filterType || 'multi-select';
  }

  async applyFilter(field: string): Promise<void> {
    const filterType = this.getFilterType(field);

    if (filterType === 'multi-select') {
      if (this.multiFilters[field]?.length === 1) {
        this.currentFilters[field] = this.multiFilters[field][0];
      } else if (this.multiFilters[field]?.length > 1) {
        this.currentFilters[field] = this.multiFilters[field].join(',');
      } else {
        this.currentFilters[field] = null;
      }
    }

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.reloadFilterOptions();
  }

  async clearFilter(field: string): Promise<void> {
    this.multiFilters[field] = [];
    this.filterSearch[field] = '';
    this.currentFilters[field] = null;

    this.showFloatingFilter = false;
    this.activeFilter = '';

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  hasAnyFilterApplied(): boolean {
    return Object.values(this.currentFilters).some(value => value !== null);
  }

  async clearAllFilters(): Promise<void> {
    this.multiFilters = {};
    this.filterSearch = {};

    Object.keys(this.currentFilters).forEach(key => {
      this.currentFilters[key] = null;
    });

    this.activeFilter = '';
    this.showFloatingFilter = false;

    this.loadFirstPage();
    await this.loadFilterOptionsInit();
  }

  async exportToCSV(): Promise<void> {
    if (!this.data.length) {
      this.toastr.info('No data available to export.');
      return;
    }

    const worksheetData = this.data.map((row: WaterSubDevAreaRow) => {
      const exportRow: Record<string, any> = {};

      this.columns.forEach((col) => {
        const value = row[col.field];
        exportRow[col.header] = Array.isArray(value) ? value.join(', ') : value ?? '';
      });

      return exportRow;
    });

    await exportRowsToExcel(
      worksheetData,
      this.columns.map((col) => col.header),
      'WaterSubDevAreaData.xlsx',
      'WaterSubDevArea'
    );
  }

  openExportDialog(): void {
    this.dialog.open(this.exportOptionsDialog, {
      width: '380px',
      disableClose: true,
    });
  }

  async downloadAllData(): Promise<void> {
    try {
      const { blob, fileName } = await this.waterSubDevAreaService.exportAllWaterSubDevAreaData();
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      link.click();
      URL.revokeObjectURL(url);
      this.toastr.success('Export downloaded successfully.');
    } catch (error: any) {
      this.toastr.error(error?.message || 'Failed to download export.');
    }
  }

  isFilterActive(field: string): boolean {
    return (this.multiFilters[field] || []).length > 0;
  }

  generateRowId(): string { 
    return `row_${Date.now()}_${Math.random().toString(36).substr(2,9)}`; 
  }
}
